/**
 * <testimonials-slider> — depth-stack card carousel.
 *
 * Each card is absolutely positioned from the track's centre by its
 * `data-d` (distance from the centred card, -3..3); the stylesheet turns that
 * into a composited translate + scale + opacity, so a move is every card
 * gliding to its next slot with nothing reflowing. All cards share one
 * layout box: the script measures the tallest card and sets `--card-h`.
 * The section renders loop clones at both ends in Liquid (with an initial
 * `data-d`), so the first paint is already correct and the loop only has to
 * re-anchor silently after a move that lands on a clone.
 *
 * Markup:
 *   <testimonials-slider data-autoplay="3000" data-clones="3">
 *     <div class="testimonials_stage">
 *       <div class="testimonials_viewport"><div class="testimonials_track">…</div></div>
 *       <div class="testimonials_nav"><button data-dir="-1"/><button data-dir="1"/></div>
 *     </div>
 *     <div class="testimonials_pagination"><button data-dot="0"/>…</div>
 *   </testimonials-slider>
 */
class TestimonialsSlider extends HTMLElement {
  connectedCallback() {
    if (this.track) return;
    this.viewport = this.querySelector('.testimonials_viewport');
    this.track = this.querySelector('.testimonials_track');
    if (!this.viewport || !this.track) return;

    this.items = Array.from(this.track.children);
    this.clones = parseInt(this.dataset.clones, 10) || 0;
    this.count = this.items.filter((item) => !item.hasAttribute('data-clone')).length;
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = this.clones;
    this.animating = false;
    this.hovered = false;
    this.visible = true;
    this.delay = parseInt(this.dataset.autoplay, 10) || 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#size();
    this.#render(false);
    this.resizeObserver = new ResizeObserver(() => this.#size());
    this.resizeObserver.observe(this.viewport);
    document.fonts?.ready.then(() => this.#size());
    if (this.count < 2) return;

    this.querySelectorAll('[data-dir]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#step(parseInt(button.dataset.dir, 10) || 1);
        this.#schedule();
      });
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.#goTo(parseInt(dot.dataset.dot, 10) || 0);
        this.#schedule();
      });
    });

    this.viewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.#step(event.key === 'ArrowRight' ? 1 : -1);
      this.#schedule();
    });

    this.track.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform' && event.target.classList?.contains('testimonials_slide')) this.#settle();
    });

    this.#bindDrag();
    this.#bindWheel();

    // Autoplay gates: hover, focus, off-screen, hidden tab, editor selection.
    this.onEnter = () => {
      this.hovered = true;
      this.#schedule();
    };
    this.onLeave = () => {
      this.hovered = false;
      this.#schedule();
    };
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.#schedule();
    });
    this.intersectionObserver.observe(this.viewport);

    this.onVisibility = () => this.#schedule();
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onBlockSelect = (event) => {
      const slide = event.target?.closest?.('.testimonials_slide');
      if (!slide || !this.track.contains(slide)) return;
      const position = this.items.indexOf(slide);
      if (position < 0) return;
      this.selected = true;
      this.index = position;
      this.#render(true);
      this.#schedule();
    };
    this.onBlockDeselect = () => {
      this.selected = false;
      this.#schedule();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.settleTimer);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  /** Horizontal drag/swipe. Vertical touch scrolling stays with the page (touch-action: pan-y). */
  #bindDrag() {
    let startX = null;
    let moved = false;

    const finish = (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      this.track.classList.remove('is-dragging');
      this.track.style.setProperty('--slide-drag', '0px');
      if (moved) {
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 0);
      }
      const threshold = Math.min(80, this.viewport.clientWidth * 0.08);
      if (Math.abs(delta) > threshold) this.#step(delta < 0 ? 1 : -1);
      this.#schedule();
    };

    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || this.animating) return;
      startX = event.clientX;
      moved = false;
      this.viewport.setPointerCapture?.(event.pointerId);
    });
    this.viewport.addEventListener('pointermove', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 6) return;
      moved = true;
      this.track.classList.add('is-dragging');
      this.track.style.setProperty('--slide-drag', `${delta}px`);
    });
    this.viewport.addEventListener('pointerup', finish);
    this.viewport.addEventListener('pointercancel', finish);
    // Swallow the click that follows a drag so links/buttons inside cards don't fire.
    this.viewport.addEventListener('click', (event) => this.suppressClick && event.preventDefault(), true);
  }

  /** Trackpad horizontal swipe, throttled to one step per gesture. */
  #bindWheel() {
    let locked = false;
    this.viewport.addEventListener(
      'wheel',
      (event) => {
        const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 10;
        if (!horizontal) return;
        event.preventDefault();
        if (locked) return;
        locked = true;
        this.#step(event.deltaX > 0 ? 1 : -1);
        this.#schedule();
        setTimeout(() => (locked = false), 700);
      },
      { passive: false }
    );
  }

  #real(position) {
    return (((position - this.clones) % this.count) + this.count) % this.count;
  }

  /** Equal boxes: every card takes the height of the tallest one at the current width. */
  #size() {
    this.style.removeProperty('--card-h');
    let tallest = 0;
    this.items.forEach((item) => {
      const card = item.querySelector('.testimonial_card') || item;
      tallest = Math.max(tallest, card.scrollHeight);
    });
    if (tallest) this.style.setProperty('--card-h', `${Math.ceil(tallest)}px`);
  }

  #render(animate) {
    this.track.classList.toggle('is-jumping', !animate);
    this.items.forEach((item, position) => {
      const distance = Math.max(-3, Math.min(3, position - this.index));
      item.setAttribute('data-d', String(distance));
    });
    const current = this.#real(this.index);
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === current);
      dot.setAttribute('aria-current', i === current ? 'true' : 'false');
    });
    if (!animate) {
      void this.track.offsetWidth; // commit the un-animated position before transitions come back
      this.track.classList.remove('is-jumping');
    }
  }

  #step(direction) {
    if (this.animating) return;
    this.animating = true;
    this.index += direction;
    this.#render(!this.reducedMotion.matches);
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.#settle(), 900);
  }

  /** Jump to real slide i by the shortest route, using the clone region when that's closer. */
  #goTo(i) {
    if (this.animating) return;
    const target = this.clones + i;
    const candidates = [target, target + this.count, target - this.count].filter(
      (candidate) => candidate >= 0 && candidate < this.items.length
    );
    const nearest = candidates.reduce((best, candidate) =>
      Math.abs(candidate - this.index) < Math.abs(best - this.index) ? candidate : best
    );
    if (nearest === this.index) return;
    this.#step(nearest - this.index);
  }

  #settle() {
    clearTimeout(this.settleTimer);
    this.animating = false;
    if (this.index >= this.count + this.clones) {
      this.index -= this.count;
      this.#render(false);
    } else if (this.index < this.clones) {
      this.index += this.count;
      this.#render(false);
    }
  }

  #schedule() {
    clearTimeout(this.timer);
    const blocked =
      !this.delay || this.reducedMotion.matches || this.hovered || this.selected || !this.visible || document.hidden;
    if (blocked) return;
    this.timer = setTimeout(() => {
      this.#step(1);
      this.#schedule();
    }, this.delay);
  }
}

if (!customElements.get('testimonials-slider')) customElements.define('testimonials-slider', TestimonialsSlider);
